import { PlusOutlined } from "@ant-design/icons";
import { AutoComplete, Button, Form, Input, Modal, Space } from "antd";
import { useRef, useState } from "react";
import { transportApi } from "../../api/transportApi";

// Sentinel value used for the "Add" option — never stored as the real value
const ADD_SENTINEL = "__LOCATION_ADD__";

interface LocationAutoCompleteProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  /** Optional label shown in the modal title, e.g. "Origin" or "Destination". */
  fieldLabel?: string;
  disabled?: boolean;
}

type CityOpt = { value: string; label?: React.ReactNode };

export const LocationAutoComplete = ({
  value,
  onChange,
  placeholder = "City / Hub",
  fieldLabel = "Location",
  disabled = false,
}: LocationAutoCompleteProps) => {
  const [options, setOptions]       = useState<CityOpt[]>([]);
  const [modalOpen, setModalOpen]   = useState(false);
  const [modalText, setModalText]   = useState("");
  // Track the raw input value so we can pass it as pre-fill even before search completes
  const typedRef = useRef("");

  const search = async (q: string) => {
    typedRef.current = q;
    try {
      const items = await transportApi.searchLocationSuggestions(q);
      const suggestions: CityOpt[] = (items ?? []).map((x: string) => ({ value: x }));

      if (suggestions.length === 0 && q.trim()) {
        // No existing suggestions — show "Add …" option that opens a modal
        setOptions([{
          value: ADD_SENTINEL,
          label: (
            <span style={{ color: "var(--primary, #1677ff)" }}>
              <PlusOutlined style={{ marginRight: 4 }} />
              Add &ldquo;{q.trim()}&rdquo;
            </span>
          ),
        }]);
      } else {
        setOptions(suggestions);
      }
    } catch { /* silent */ }
  };

  const openAddModal = (prefill: string) => {
    setModalText(prefill.trim());
    setModalOpen(true);
  };

  const handleSelect = (val: string) => {
    if (val === ADD_SENTINEL) {
      // Do NOT propagate the sentinel — open modal instead
      openAddModal(typedRef.current);
      return;
    }
    onChange?.(val);
  };

  const handleChange = (v: string) => {
    // Block the sentinel from ever reaching the parent
    if (v === ADD_SENTINEL) return;
    typedRef.current = v;
    onChange?.(v ?? "");
  };

  const handleModalSave = () => {
    const trimmed = modalText.trim();
    if (!trimmed) return;
    onChange?.(trimmed);
    setModalOpen(false);
    setOptions([]);
  };

  return (
    <>
      <AutoComplete
        disabled={disabled}
        value={value}
        onChange={handleChange}
        onSelect={handleSelect}
        options={options}
        onSearch={(q) => void search(q)}
        onFocus={() => { if (!value && options.length === 0) void search(""); }}
        onKeyDown={(e) => {
          // Enter with the sentinel as the only option → open modal
          if (
            e.key === "Enter"
            && options.length === 1
            && options[0].value === ADD_SENTINEL
            && typedRef.current.trim()
          ) {
            e.preventDefault();
            openAddModal(typedRef.current);
          }
        }}
        placeholder={placeholder}
        filterOption={false}
        style={{ width: "100%" }}
      />

      <Modal
        title={`Add ${fieldLabel}`}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        afterOpenChange={(vis) => { if (vis) setModalText(typedRef.current.trim()); }}
        destroyOnClose
        width={420}
        footer={
          <Space style={{ justifyContent: "flex-end", width: "100%" }}>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              disabled={!modalText.trim()}
              onClick={handleModalSave}
            >
              Use &ldquo;{modalText.trim()}&rdquo;
            </Button>
          </Space>
        }
      >
        <Form layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item label={`${fieldLabel} Name`} required>
            <Input
              autoFocus
              value={modalText}
              onChange={(e) => setModalText(e.target.value)}
              placeholder="e.g. Delhi"
              onPressEnter={handleModalSave}
            />
          </Form.Item>
          <div style={{ fontSize: 12, opacity: 0.6 }}>
            This location will be saved as free text. It will appear as a suggestion in future searches.
          </div>
        </Form>
      </Modal>
    </>
  );
};
